import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Mail, Database, Lock, Eye, UserCheck, Clock, Globe } from 'lucide-react';

const PrivacyPolicyPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <Link to="/" className="flex items-center gap-2 text-gray-600 hover:text-gray-900">
            <ArrowLeft className="h-5 w-5" />
            <span className="font-semibold">REVELIAE</span>
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Title */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl mb-4">
              <Shield className="h-8 w-8 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Politique de Confidentialité</h1>
            <p className="text-gray-500">Dernière mise à jour : 25 février 2026</p>
          </div>

          {/* Content */}
          <div className="prose prose-gray max-w-none">
            <section className="mb-8 p-6 bg-white rounded-2xl border border-gray-200">
              <div className="flex items-center gap-3 mb-4">
                <Database className="h-6 w-6 text-blue-500" />
                <h2 className="text-xl font-bold text-gray-900 m-0">1. Collecte des données</h2>
              </div>
              <p className="text-gray-600 mb-4">
                REVELIAE collecte les données personnelles suivantes dans le cadre de son service :
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-2">
                <li><strong>Données d'identification</strong> : nom, prénom, adresse email</li>
                <li><strong>Données de naissance</strong> : date, heure et lieu de naissance (pour les analyses astrologiques et numérologiques)</li>
                <li><strong>Données de connexion</strong> : adresse IP, type de navigateur, pages visitées</li>
                <li><strong>Données de paiement</strong> : traitées de manière sécurisée par nos partenaires Stripe et PayPal</li>
              </ul>
            </section>

            <section className="mb-8 p-6 bg-white rounded-2xl border border-gray-200">
              <div className="flex items-center gap-3 mb-4">
                <Eye className="h-6 w-6 text-purple-500" />
                <h2 className="text-xl font-bold text-gray-900 m-0">2. Utilisation des données</h2>
              </div>
              <p className="text-gray-600 mb-4">Vos données sont utilisées pour :</p>
              <ul className="list-disc list-inside text-gray-600 space-y-2">
                <li>Fournir nos services d'analyse (numérologie, astrologie, tarot, chiromancie)</li>
                <li>Personnaliser votre expérience utilisateur</li>
                <li>Gérer votre compte et vos abonnements</li>
                <li>Envoyer des notifications (si vous l'avez accepté)</li>
                <li>Améliorer nos services et effectuer des analyses statistiques</li>
                <li>Respecter nos obligations légales</li>
              </ul>
            </section>

            <section className="mb-8 p-6 bg-white rounded-2xl border border-gray-200">
              <div className="flex items-center gap-3 mb-4">
                <Lock className="h-6 w-6 text-green-500" />
                <h2 className="text-xl font-bold text-gray-900 m-0">3. Protection des données</h2>
              </div>
              <p className="text-gray-600 mb-4">
                Nous mettons en œuvre des mesures de sécurité appropriées pour protéger vos données :
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-2">
                <li>Chiffrement SSL/TLS pour toutes les communications</li>
                <li>Stockage sécurisé des données sur des serveurs protégés</li>
                <li>Accès restreint aux données personnelles</li>
                <li>Double authentification disponible pour votre compte</li>
                <li>Audits de sécurité réguliers</li>
              </ul>
            </section>

            <section className="mb-8 p-6 bg-white rounded-2xl border border-gray-200">
              <div className="flex items-center gap-3 mb-4">
                <UserCheck className="h-6 w-6 text-amber-500" />
                <h2 className="text-xl font-bold text-gray-900 m-0">4. Vos droits (RGPD)</h2>
              </div>
              <p className="text-gray-600 mb-4">
                Conformément au Règlement Général sur la Protection des Données (RGPD), vous disposez des droits suivants :
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-2">
                <li><strong>Droit d'accès</strong> : obtenir une copie de vos données personnelles</li>
                <li><strong>Droit de rectification</strong> : corriger vos données inexactes</li>
                <li><strong>Droit à l'effacement</strong> : demander la suppression de vos données</li>
                <li><strong>Droit à la portabilité</strong> : recevoir vos données dans un format structuré</li>
                <li><strong>Droit d'opposition</strong> : vous opposer au traitement de vos données</li>
                <li><strong>Droit de limitation</strong> : limiter le traitement de vos données</li>
              </ul>
              <p className="text-gray-600 mt-4">
                Pour exercer ces droits, contactez-nous à : <a href="mailto:privacy@reveliae.com" className="text-blue-600 hover:underline">privacy@reveliae.com</a>
              </p>
            </section>

            <section className="mb-8 p-6 bg-white rounded-2xl border border-gray-200">
              <div className="flex items-center gap-3 mb-4">
                <Clock className="h-6 w-6 text-rose-500" />
                <h2 className="text-xl font-bold text-gray-900 m-0">5. Conservation des données</h2>
              </div>
              <p className="text-gray-600 mb-4">Vos données sont conservées pendant :</p>
              <ul className="list-disc list-inside text-gray-600 space-y-2">
                <li><strong>Données de compte</strong> : jusqu'à la suppression de votre compte + 3 ans</li>
                <li><strong>Données de facturation</strong> : 10 ans (obligation légale)</li>
                <li><strong>Données de connexion</strong> : 1 an</li>
                <li><strong>Cookies</strong> : voir notre politique de cookies</li>
              </ul>
            </section>

            <section className="mb-8 p-6 bg-white rounded-2xl border border-gray-200">
              <div className="flex items-center gap-3 mb-4">
                <Globe className="h-6 w-6 text-cyan-500" />
                <h2 className="text-xl font-bold text-gray-900 m-0">6. Transfert des données</h2>
              </div>
              <p className="text-gray-600">
                Vos données peuvent être transférées vers des prestataires situés en dehors de l'Union Européenne 
                (notamment pour les services d'hébergement et de paiement). Ces transferts sont encadrés par des 
                clauses contractuelles types approuvées par la Commission Européenne ou par des décisions d'adéquation.
              </p>
            </section>

            <section className="p-6 bg-white rounded-2xl border border-gray-200">
              <div className="flex items-center gap-3 mb-4">
                <Mail className="h-6 w-6 text-indigo-500" />
                <h2 className="text-xl font-bold text-gray-900 m-0">7. Contact</h2>
              </div>
              <p className="text-gray-600 mb-4">
                Pour toute question concernant cette politique de confidentialité ou vos données personnelles :
              </p>
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="text-gray-700 font-medium">REVELIAE</p>
                <p className="text-gray-600">Email : <a href="mailto:privacy@reveliae.com" className="text-blue-600 hover:underline">privacy@reveliae.com</a></p>
                <p className="text-gray-600">Délégué à la Protection des Données (DPO) : dpo@reveliae.com</p>
              </div>
            </section>
          </div>

          {/* Back link */}
          <div className="mt-12 text-center">
            <Link to="/" className="text-gray-500 hover:text-gray-700">
              ← Retour à l'accueil
            </Link>
          </div>
        </motion.div>
      </main>
    </div>
  );
};

export default PrivacyPolicyPage;
